import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  IconButton,
} from "@mui/material";
import {
  Handshake as PartnershipIcon,
  Business as BusinessIcon,
  MonetizationOn as RevenueIcon,
  SupportAgent as SupportIcon,
  Group as TeamIcon,
  TrendingUp as GrowthIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CurrencyRupee,
  Close as CloseIcon,
} from "@mui/icons-material";
import PartnerWithUsImg from "../Images/PartnerWithUs.png";
import BookingDialog from "./BookingDialog";
import { Dialog, DialogTitle, DialogContent, DialogContentText, TextField } from "@mui/material";
import CtBtn from "./CustomTool/CtBtn";
import CtTxt from "./CustomTool/CtTxt";
import CtTxtNum from "./CustomTool/CtTxtNum";
import AlertResponsiveDialog from "./CustomTool/AlertResponsiveDialog";
import processing from "../gif/processing.gif";
import HoCtToastContainer from "../HOC/HoCtToastContainer";

const PartnerWithUs = ({ notifyProcessing, closeUpdateProcessing, toastMsg, toastErrorMsg }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openBooking, setOpenBooking] = React.useState(false);
  const [openApply, setOpenApply] = React.useState(false);
  const [showARD, setShowARD] = React.useState(false);
  const [ardMsg, setArdMsg] = React.useState("");

  const [formData, setFormData] = React.useState({
    company: "",
    contact: "",
    email: "",
    phone: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    // Validation
    if (!formData.contact.trim()) {
      toastErrorMsg("Please enter Full Name.");
      document.getElementById('contact')?.focus();
      return;
    }

    if (!formData.email.trim()) {
      toastErrorMsg("Please enter Email Address.");
      document.getElementById('email')?.focus();
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toastErrorMsg("Please enter a valid email address.");
      document.getElementById('email')?.focus();
      return;
    }

    if (!formData.phone.trim()) {
      toastErrorMsg("Please enter Phone Number.");
      document.getElementById('phone')?.focus();
      return;
    }

    if (formData.phone.length !== 10) {
      toastErrorMsg("Phone number must be exactly 10 digits.");
      document.getElementById('phone')?.focus();
      return;
    }

    if (!formData.company.trim()) {
      toastErrorMsg("Please enter Company Name.");
      document.getElementById('company')?.focus();
      return;
    }

    notifyProcessing();

    try {
      // Mock submission for now
      setTimeout(() => {
        setOpenApply(false);
        setArdMsg("Application submitted successfully! Our team will contact you soon.");
        setShowARD(true);
        setFormData({ company: "", contact: "", email: "", phone: "" });
        closeUpdateProcessing();
      }, 1000);
    } catch (err) {
      console.error("Submission error", err);
      setArdMsg("Something went wrong. Please try again.");
      setShowARD(true);
      closeUpdateProcessing();
    }
  };

  const partnershipTypes = [
    {
      title: "Reseller Partners",
      description:
        "Sell Rite Account solutions to your clients with exclusive discounts and support",
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      benefits: [
        "30% revenue share",
        "Dedicated support",
        "Marketing materials",
        "Training programs",
      ],
    },

    {
      title: "Referral Partners",
      description:
        "Earn commissions by referring businesses to our accounting solutions and grow your income faster.",
      icon: <CurrencyRupee sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      benefits: [
        "15% commission",
        "No technical expertise needed",
        "Lead tracking dashboard",
        "Flexible terms",
      ],
    },
  ];

  const benefits = [
    {
      title: "Revenue Opportunities",
      description:
        "Generate additional income streams with competitive commission structures",
      icon: <RevenueIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Dedicated Support",
      description:
        "Access our partner success team for technical and sales assistance",
      icon: <SupportIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Marketing Resources",
      description:
        "Leverage our brand assets, campaigns, and co-marketing opportunities",
      icon: <GrowthIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Training Programs",
      description:
        "Comprehensive training to ensure your team is fully equipped",
      icon: <TeamIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const processSteps = [
    {
      step: "1",
      title: "Apply",
      description: "Fill out our partner application form",
    },
    {
      step: "2",
      title: "Review",
      description: "Our team will evaluate your application",
    },
    {
      step: "3",
      title: "Onboard",
      description: "Complete training and get access to resources",
    },
    {
      step: "4",
      title: "Launch",
      description: "Start partnering and growing together",
    },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
          py: 6,
          mb: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                label="Partnership Opportunities"
                color="primary"
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Partner With Us
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6, maxWidth: 500 }}
              >
                Join our network of partners and grow your business while
                helping others streamline their accounting processes with Rite
                Account.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <CtBtn variant="contained" size="large" onClick={() => setOpenApply(true)} label="Apply Now" />
                {/* <Button variant="outlined" size="large">
                  Learn More
                </Button> */}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={PartnerWithUsImg}
                alt="Partner With Us"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Partnership Types */}
      <Container maxWidth="lg" sx={{ mb: 15 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Partnership Programs
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
          >
            Choose the partnership model that works best for your business
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {partnershipTypes.map((type, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card
                elevation={2}
                sx={{
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.8
                  )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "all 0.3s ease-in-out",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 16px 40px -10px ${alpha(type.color, 0.2)}`,
                    borderColor: alpha(type.color, 0.3),
                  },
                }}
              >
                <Stack direction="row" alignItems={"center"} spacing={2}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      background: `linear-gradient(135deg, ${alpha(
                        type.color,
                        0.1
                      )} 0%, transparent 100%)`,
                      color: alpha(theme.palette.text.primary, 0.7),
                      transition: "all 0.3s ease",
                    }}
                  >
                    {type.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    {type.title}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, mb: 1, lineHeight: 1.6, fontSize: 15 }}
                >
                  {type.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="subtitle2"
                  fontSize={16}
                  fontWeight={600}
                  mb={1}
                >
                  Key Benefits:
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {type.benefits.map((benefit, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckIcon color="primary" sx={{ fontSize: 22 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit}
                        primaryTypographyProps={{ fontSize: 15 }}
                      />
                    </ListItem>
                  ))}
                </List>

                <CtBtn
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 3, borderRadius: 2, display: 'none' }}
                  color="primary"
                  label="Learn More"
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
          py: 8,
          mb: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Partner Benefits
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mx: "auto", lineHeight: 1.6 }}
            >
              Why partnering with Rite Account is the right choice for your
              business
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent={"center"}>
            {benefits.map((benefit, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    borderRadius: 4,
                    background: "transparent",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      background: `linear-gradient(135deg, ${alpha(
                        benefit.color,
                        0.1
                      )} 0%, transparent 100%)`,
                      color: alpha(theme.palette.text.primary, 0.7),
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} mb={1.5}>
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Partnership Process
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
          >
            Simple steps to become a Rite Account partner
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent={"center"} rowSpacing={6}>
          {processSteps.map((step, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: 4,
                  height: "90%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.8
                  )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  position: "relative",
                  overflow: "hidden",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, transparent 100%)`,
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: 24,
                  }}
                >
                  {step.step}
                </Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Section */}
      <Container maxWidth="md" sx={{ mb: 4, mt: 4 }}>
        <Paper
          elevation={4}
          sx={{
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box textAlign="center" mb={4}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to Partner With Us?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 500, mx: "auto" }}
            >
              Join our growing network of partners and start growing your
              business today.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <EmailIcon color="primary" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  partners@riteaccount.com
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PhoneIcon color="primary" sx={{ mr: 2 }} />
                <Typography variant="body1">+1 (800) 123-4567</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                <LocationIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
                <Typography variant="body1">
                  123 Business Avenue
                  <br />
                  San Francisco, CA 94107
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CtBtn
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => setOpenBooking(true)}
                  label="Schedule a Meeting"
                />
                <CtBtn
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={() => setOpenApply(true)}
                  label="Apply Now"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* CTA Section */}
      {/* ... omitted for brevity ... */}

      <BookingDialog open={openBooking} onClose={() => setOpenBooking(false)} />

      {openApply && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 24, width: '100%', maxWidth: 500 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5">Apply for Partnership</Typography>
              <CtBtn onClick={() => setOpenApply(false)} label="Close" color="inherit" size="small" variant="text" />
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please fill out this short form and our team will get back to you shortly.
            </Typography>
            <Box component="form" onSubmit={handleApplySubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CtTxt
                id="company"
                label="Company Name"
                value={formData.company}
                handleOnChange={handleInputChange}
                maxLength={100}
                required
                fullWidth
              />
              <CtTxt
                id="contact"
                label="Contact Name"
                value={formData.contact}
                handleOnChange={handleInputChange}
                maxLength={100}
                required
                fullWidth
              />
              <CtTxt
                id="email"
                label="Email"
                value={formData.email}
                handleOnChange={handleInputChange}
                maxLength={100}
                required
                fullWidth
              />
              <CtTxtNum
                id="phone"
                label="Phone Number"
                value={formData.phone}
                handleOnChange={handleInputChange}
                maxLength={10}
                required
                fullWidth
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <CtBtn onClick={() => setOpenApply(false)} label="Cancel" color="inherit" variant="outlined" />
                <CtBtn
                  label="Submit Application"
                  variant="contained"
                  color="primary"
                  onClick={handleApplySubmit}
                />
              </Box>
            </Box>
          </div>
        </div>
      )}

      {showARD && (
        <AlertResponsiveDialog
          defaultOpenDialog={true}
          labelAgree="Ok"
          alertTitle="Status"
          alertMessage={ardMsg}
          handleOnClickYes={() => setShowARD(false)}
          onYesClickCloseIfExeSuccessfully={true}
          style={{ display: "none" }}
        />
      )}
    </Box>
  );
};

export default HoCtToastContainer(PartnerWithUs);
